import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { WeekendModiferType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { weekendModifier: WeekendModiferType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: WeekendModiferType) => void
  handleFormUpdate: (form: WeekendModiferType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const WeekendModifier: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<WeekendModiferType>()

  const onSubmit = (data: WeekendModiferType) => {
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      // color: "#ffffff",
      // background: "#333333",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: WeekendModiferType) => {
    props.setTitle('Update Weekend Modifier')
    setValue("id", e.id)
    setValue("dayName", e.dayName)
    setValue("dayValue", e.dayValue)
    setValue("modifierType", e.modifierType)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      dayName: "",
      dayValue: undefined,
      modifierType: "",
      id: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title={props.title}
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Day Name</label>
              <input
                className={
                  (errors.dayName ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                placeholder="Enter Day Name"
                {...register("dayName", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Day Value</label>
              <input
                className={
                  (errors.dayValue ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Day Value"
                {...register("dayValue", { required: true })}
              />
            </fieldset>
          </div>
          <fieldset>
            <label className="text-xs dark:text-primary-8 text-light-1">Modifier Type</label>
            <input
              className={
                (errors.modifierType ? "border-red-400" : "dark:border-secondary-2") +
                " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
              }
              type="text"
              placeholder="Enter Modifier Type"
              {...register("modifierType", { required: true })}
            />
          </fieldset>
          <button
            onClick={handleSubmit(onSubmit)}
            className=" bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.weekendModifier.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>{e.dayName}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.dayValue}</td>
              <td className={"whitespace-nowrap p-4 w-80"}>{e.modifierType}</td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    onClick={() => populateData(e)}
                    className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                  />
                  <TrashIcon
                    onClick={() => onDelete(e.id)}
                    className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
